import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, File, Image, FileText, Video, Music, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

// Move allowedTypes outside component to avoid dependency issues
const allowedTypes = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  document: ['application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  video: ['video/mp4', 'video/webm', 'video/quicktime'],
  audio: ['audio/mpeg', 'audio/wav', 'audio/ogg']
};

const MediaUpload = ({ onFilesSelected, maxFiles = 5, maxSize = 10 * 1024 * 1024 }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const getFileIcon = (file) => {
    const type = file.type;
    if (allowedTypes.image.includes(type)) return Image;
    if (allowedTypes.document.includes(type)) return FileText;
    if (allowedTypes.video.includes(type)) return Video;
    if (allowedTypes.audio.includes(type)) return Music;
    return File;
  };

  const getFileCategory = useCallback((file) => {
    const type = file.type;
    if (allowedTypes.image.includes(type)) return 'image';
    if (allowedTypes.document.includes(type)) return 'document';
    if (allowedTypes.video.includes(type)) return 'video';
    if (allowedTypes.audio.includes(type)) return 'audio';
    return 'other';
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = useCallback((file) => {
    if (file.size > maxSize) {
      toast.error(`File "${file.name}" is too large. Maximum size is ${formatFileSize(maxSize)}.`);
      return false;
    }

    const allAllowedTypes = Object.values(allowedTypes).flat();
    if (!allAllowedTypes.includes(file.type)) {
      toast.error(`File type "${file.type}" is not supported.`);
      return false;
    }

    return true;
  }, [maxSize]);

  const handleFiles = useCallback((files) => {
    const fileArray = Array.from(files);
    
    if (selectedFiles.length + fileArray.length > maxFiles) {
      toast.error(`You can only upload up to ${maxFiles} files.`);
      return;
    }

    const validFiles = fileArray.filter(validateFile);
    
    if (validFiles.length > 0) {
      const newFiles = validFiles.map(file => ({
        file,
        id: Date.now() + Math.random(),
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        category: getFileCategory(file)
      }));

      setSelectedFiles(prev => [...prev, ...newFiles]);
      onFilesSelected([...selectedFiles, ...newFiles]);
      toast.success(`${validFiles.length} file(s) added successfully.`);
    }
  }, [selectedFiles, maxFiles, onFilesSelected, validateFile, getFileCategory]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (fileId) => {
    const updatedFiles = selectedFiles.filter(f => f.id !== fileId);
    setSelectedFiles(updatedFiles);
    onFilesSelected(updatedFiles);
    
    // Clean up preview URLs
    const fileToRemove = selectedFiles.find(f => f.id === fileId);
    if (fileToRemove?.preview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
  };

  const clearAllFiles = () => {
    selectedFiles.forEach(f => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setSelectedFiles([]);
    onFilesSelected([]);
  };

  return (
    <div className="w-full">
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-4 sm:p-6 transition-colors cursor-pointer ${
          dragActive 
            ? 'border-primary-500 bg-primary-50' 
            : 'border-gray-300 hover:border-gray-400 bg-gray-50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf,text/plain,.doc,.docx,video/*,audio/*"
          onChange={handleFileInput}
          className="hidden"
        />
        
        <div className="text-center">
          <Upload className={`mx-auto h-8 w-8 sm:h-12 sm:w-12 mb-3 ${
            dragActive ? 'text-primary-600' : 'text-gray-400'
          }`} />
          <p className="text-sm sm:text-base font-medium text-gray-900 mb-1">
            {dragActive ? 'Drop files here' : 'Upload media files'}
          </p>
          <p className="text-xs sm:text-sm text-gray-500 mb-2">
            Drag & drop or click to select files
          </p>
          <p className="text-xs text-gray-400">
            Images, PDFs, Documents, Videos, Audio • Max {formatFileSize(maxSize)} each
          </p>
        </div>
      </div>

      {/* File List */}
      {selectedFiles.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-900">
              Selected Files ({selectedFiles.length}/{maxFiles})
            </h4>
            <button
              onClick={clearAllFiles}
              className="text-xs text-red-600 hover:text-red-700 transition-colors"
            >
              Clear All
            </button>
          </div>
          
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {selectedFiles.map((fileObj) => {
              const FileIcon = getFileIcon(fileObj.file);
              
              return (
                <div
                  key={fileObj.id}
                  className="flex items-center space-x-3 p-2 sm:p-3 bg-white border border-gray-200 rounded-lg"
                >
                  {/* File Preview/Icon */}
                  <div className="flex-shrink-0">
                    {fileObj.preview ? (
                      <img
                        src={fileObj.preview}
                        alt={fileObj.file.name}
                        className="w-8 h-8 sm:w-10 sm:h-10 object-cover rounded"
                      />
                    ) : (
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded flex items-center justify-center">
                        <FileIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                      </div>
                    )}
                  </div>
                  
                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {fileObj.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(fileObj.file.size)} • {fileObj.category}
                    </p>
                  </div>
                  
                  {/* Remove Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(fileObj.id);
                    }}
                    className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload Guidelines */}
      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-700">
            <p className="font-medium mb-1">Supported file types:</p>
            <ul className="space-y-0.5">
              <li>• Images: JPEG, PNG, GIF, WebP</li>
              <li>• Documents: PDF, TXT, DOC, DOCX</li>
              <li>• Videos: MP4, WebM, MOV</li>
              <li>• Audio: MP3, WAV, OGG</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaUpload;
